import { useState, useEffect, Suspense } from "react"
import { vclog } from '../hooks/vc-log'
import { lazy, type ComponentType } from 'react'
import type { ComponentProps } from 'react'
import type { Participant } from '@vc/models'
import { useMeetingDialogs, useBreakoutRooms } from '@vc/viewmodels'
import { ConfirmDialog } from '@vc/views/dialogs/ConfirmDialog'
import { MobileOverlayPanel } from '@/clientWeb/components/ui/mobile-overlay-panel'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { useMeetingPreferencesStore } from '../store/meeting-preferences.store'
import { useMeetingLayoutStore } from '../store/meeting-layout.store'
import {
  useMeetingPollsStore,
  selectActivePoll,
} from '../store/meeting-polls.store'
import { useMeetingSurveysStore } from '../store/meeting-surveys.store'
import { useHostPermissionsStore } from '../store/host-permissions.store'
import { useMeetingHostControlsStore } from '../store/meeting-host-controls.store'
import { getPublishedCameraTrack, preloadBackgroundImage, warmSegmentation } from '../services/virtual-background'
import { isOwnPoll } from '../services/poll-prompt-queue'
import { pollResultFor } from '../services/poll-results'
import { useResponsiveDevice } from '@vc/viewmodels/hooks/useResponsiveDevice'

/**
 * Give a lazy dialog a Suspense boundary of its own.
 *
 * Without one, the nearest boundary is `SafeSuspense` around the whole meeting
 * with `fallback={null}` — so the first time any dialog opened, the video grid
 * and controls vanished until its chunk arrived. `null` is the right fallback
 * for a dialog (there is nothing to show before it exists); what matters is
 * that the suspension is contained here instead of blanking the meeting.
 */
function withDialogSuspense<P extends object>(Lazy: ComponentType<P>): ComponentType<P> {
  return function SuspendedDialog(props: P) {
    return (
      <Suspense fallback={null}>
        <Lazy {...props} />
      </Suspense>
    )
  }
}

const CreatePollDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/CreatePollDialog').then((m) => ({ default: m.CreatePollDialog })),
))
const PollVotingDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/PollVotingDialog').then((m) => ({ default: m.PollVotingDialog })),
))
const PollResultsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/PollResultsDialog').then((m) => ({ default: m.PollResultsDialog })),
))
const BackgroundEffectsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/BackgroundEffectsDialog').then((m) => ({
    default: m.BackgroundEffectsDialog,
  })),
))
const AddNewVideoInputDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/AddNewVideoInputDialog').then((m) => ({
    default: m.AddNewVideoInputDialog,
  })),
))
const EngagementControlsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/EngagementControlsDialog').then((m) => ({
    default: m.EngagementControlsDialog,
  })),
))
const EndWebinarDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs/EndWebinarDialog').then((m) => ({ default: m.EndWebinarDialog })),
))
const CreateBreakoutRoomsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/controlBar/components/breakout').then((m) => ({
    default: m.CreateBreakoutRoomsDialog,
  })),
))
const BreakoutRoomsAssignment = withDialogSuspense(lazy(() =>
  import('@vc/views/controlBar/components/breakout').then((m) => ({
    default: m.BreakoutRoomsAssignment,
  })),
))
const SettingsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.SettingsDialog })),
))
const HelpDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.HelpDialog })),
))
const AboutDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.AboutDialog })),
))
const ShortcutsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.ShortcutsDialog })),
))
const ReportDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.ReportDialog })),
))
const AdjustViewDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.AdjustViewDialog })),
))
const HostControlsDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.HostControlsDialog })),
))
const CreateSurveyDialog = withDialogSuspense(lazy(() =>
  import('@vc/views/dialogs').then((m) => ({ default: m.CreateSurveyDialog })),
))

/**
 * The distinct chunks the dialogs above split into.
 *
 * A lazy component SUSPENDS the first time it renders, and the nearest boundary
 * was `SafeSuspense` at the root of MeetingScreen with `fallback={null}` — so
 * opening any dialog blanked the whole meeting, video grid and controls
 * included, until the chunk arrived. The boundary at the bottom of this file
 * contains that; warming the chunks while the browser is idle means there is
 * usually nothing left to fetch by the time anyone clicks.
 */
const DIALOG_CHUNKS: Array<() => Promise<unknown>> = [
  () => import('@vc/views/dialogs'),
  () => import('@vc/views/controlBar/components/breakout'),
  () => import('@vc/views/dialogs/CreatePollDialog'),
  () => import('@vc/views/dialogs/PollVotingDialog'),
  () => import('@vc/views/dialogs/PollResultsDialog'),
  () => import('@vc/views/dialogs/BackgroundEffectsDialog'),
  () => import('@vc/views/dialogs/AddNewVideoInputDialog'),
  () => import('@vc/views/dialogs/EngagementControlsDialog'),
  () => import('@vc/views/dialogs/EndWebinarDialog'),
]

/** Warm the dialog chunks once the meeting has settled. Failures are ignored —
 *  this is an optimisation, and `lazy` will fetch again on demand. */
function usePrefetchDialogs() {
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (id: number) => void
    }
    const warm = () => {
      for (const load of DIALOG_CHUNKS) void load().catch(() => {})
    }
    const id = w.requestIdleCallback ? w.requestIdleCallback(warm) : window.setTimeout(warm, 1500)
    return () => {
      if (w.cancelIdleCallback) w.cancelIdleCallback(id)
      else window.clearTimeout(id)
    }
  }, [])
}

type BreakoutAssignableParticipant = {
  id: string
  name: string
  role: 'host' | 'participant'
}

type BreakoutSettings = {
  roomCount: number
  assignmentType: 'automatic' | 'manual' | 'self'
  participantLimit: number
}

type ActiveBreakoutRoom = {
  id: string
  name: string
  isActive: boolean
  participants: Array<{
    id: string
    name: string
    isHost?: boolean
    audioOn?: boolean
    videoOn?: boolean
  }>
}

type AdjustViewDialogProps = ComponentProps<typeof AdjustViewDialog>

/** The UI's word for how people are assigned, as the wire spells it. */
function assignmentWireValue(
  t: 'automatic' | 'manual' | 'self' | undefined,
): 'auto' | 'manual' | 'self' {
  return t === 'manual' ? 'manual' : t === 'self' ? 'self' : 'auto'
}

export interface MeetingScreenDialogsProps {
  isMobileOrLandscape: boolean
  /** Whether this meeting has captions (it runs transcription); see ShortcutsDialog. */
  captionsAvailable: boolean
  localParticipant: Participant
  meetingUserName: string
  onLeaveMeeting: () => void
  breakoutAssignableParticipants: BreakoutAssignableParticipant[]
  /**
   * Announce the rooms to the meeting.
   *
   * Starting them used to be a local `setActiveBreakoutRooms` and nothing else,
   * so the server never heard, nobody was broadcast to, and no participant was
   * ever told they had been assigned anywhere. This is the call that makes it
   * real.
   */
  onStartBreakoutRooms: (
    rooms: Array<{ id: string; name: string; participantIds: string[] }>,
    assignment: 'auto' | 'manual' | 'self',
    /** How many fit in one room, so the room can hold people to it. */
    capacity?: number | null,
  ) => void
  /** Close them all and bring everybody back. */
  onEndBreakoutRooms: () => void
  /** Move one person while the rooms are already running. */
  onMoveBreakoutParticipant: (userId: string, roomId: string | null) => void
  /** The rooms as they stand, so managing a running set shows who is where. */
  activeBreakoutRooms: Array<{ id: string; name: string; participants: Array<{ id: string }> }>
  /** A room has no seat left for the person being placed. */
  onBreakoutRoomFull: (roomId: string) => void
  /** The room says they are running — Start retires, End takes its place. */
  breakoutStarted: boolean
  /** Everyone in the room by id, this client included — poll participation is counted from it. */
  presentParticipantIds: readonly string[]
  /** Someone other than this client is sharing a screen right now. */
  othersScreenSharing: boolean
  /** Someone other than this client has a document on the stage right now. */
  othersSharingFiles: boolean

  /** Extra cameras this client is publishing, and how to add / remove one. */
  videoInputs?: Array<{ sourceId: string; deviceId: string; label: string }>
  maxVideoInputs?: number
  onAddVideoInput?: (deviceId: string, label: string) => Promise<void>
  onRemoveVideoInput?: (sourceId: string) => void

  // Demo arrays — random fixed tile-occupancy previews shown in the AdjustView dialog
  autoGridTiles: boolean[]
  tiledGridTiles: boolean[]
}

function assignParticipantsRoundRobin(
  settings: BreakoutSettings,
  pool: BreakoutAssignableParticipant[],
): ActiveBreakoutRoom[] {
  const rooms: ActiveBreakoutRoom[] = Array.from({ length: settings.roomCount }, (_, i) => ({
    id: `room-${i + 1}`,
    name: `Room ${i + 1}`,
    isActive: true,
    participants: [],
  }))

  const maxTotal = Math.max(0, settings.roomCount * settings.participantLimit)
  const assignable = pool.slice(0, maxTotal)

  let roomIndex = 0
  for (const p of assignable) {
    let safety = 0
    while (
      rooms[roomIndex]!.participants.length >= settings.participantLimit &&
      safety < rooms.length
    ) {
      roomIndex = (roomIndex + 1) % rooms.length
      safety += 1
    }
    if (rooms[roomIndex]!.participants.length >= settings.participantLimit) break

    rooms[roomIndex]!.participants.push({
      id: p.id,
      name: p.name,
      isHost: p.role === 'host',
      audioOn: true,
      videoOn: true,
    })
    roomIndex = (roomIndex + 1) % rooms.length
  }

  return rooms
}

export function MeetingScreenDialogs(props: MeetingScreenDialogsProps) {
  const {
    isMobileOrLandscape,
    localParticipant,
    meetingUserName,
    onLeaveMeeting,
    breakoutAssignableParticipants,
    onStartBreakoutRooms,
    onEndBreakoutRooms,
    onMoveBreakoutParticipant,
    activeBreakoutRooms,
    onBreakoutRoomFull,
    breakoutStarted,
    presentParticipantIds,
    othersScreenSharing,
    othersSharingFiles,

    autoGridTiles,
    tiledGridTiles,
  } = props
  const t = useTranslation()

  const activePollId = useMeetingPollsStore((s) => s.activePollId)
  const activePoll = useMeetingPollsStore(selectActivePoll)
  const pollVotes = useMeetingPollsStore((s) => s.pollVotes)
  const handleCreatePoll = useMeetingPollsStore((s) => s.handleCreatePoll)
  const handleVotePoll = useMeetingPollsStore((s) => s.handleVotePoll)
  const handleEndPoll = useMeetingPollsStore((s) => s.handleEndPoll)
  const createSurvey = useMeetingSurveysStore((s) => s.createSurvey)

  // Host-controls permissions live in the feature layer — each flag is
  // selected individually so Zustand's referential equality keeps renders
  // stable (a single object selector would loop). The dialog itself stays
  // prop-driven and unaware of any store.
  const sendChat = useMeetingHostControlsStore((s) => s.sendChat)
  const privateChat = useMeetingHostControlsStore((s) => s.privateChat)
  const sendFiles = useMeetingHostControlsStore((s) => s.sendFiles)
  const useMicrophone = useMeetingHostControlsStore((s) => s.useMicrophone)
  const useVideo = useMeetingHostControlsStore((s) => s.useVideo)
  const useEmojis = useMeetingHostControlsStore((s) => s.useEmojis)
  const shareScreen = useMeetingHostControlsStore((s) => s.shareScreen)
  const allowChat = useMeetingHostControlsStore((s) => s.allowChat)
  const allowAnnotation = useMeetingHostControlsStore((s) => s.allowAnnotation)
  const setHostControl = useMeetingHostControlsStore((s) => s.setControl)
  // Engagement-only, and local: the room tracks neither.
  const allowHandRaise = useHostPermissionsStore((s) => s.allowHandRaise)
  const allowQuestions = useHostPermissionsStore((s) => s.allowQuestions)
  const setEngagementPermission = useHostPermissionsStore((s) => s.setPermission)
  const hostPermissions = {
    sendChat,
    privateChat,
    sendFiles,
    useMicrophone,
    useVideo,
    useEmojis,
    shareScreen,
    allowChat,
    allowAnnotation,
  }
  // Switching screen or file sharing off also stops the shares already running
  // (the publish effect in MeetingScreen), so while somebody else is sharing
  // the host is asked first instead of learning it from the emptied stage.
  // Both dialogs come through here, so neither can skip the question.
  const [pendingShareOff, setPendingShareOff] = useState<'shareScreen' | 'sendFiles' | null>(null)
  const changeHostControl = (key: keyof typeof hostPermissions, value: boolean) => {
    if (
      !value &&
      ((key === 'shareScreen' && othersScreenSharing) || (key === 'sendFiles' && othersSharingFiles))
    ) {
      setPendingShareOff(key)
      return
    }
    setHostControl(key, value)
  }
  // The engagement dialog and host-controls dialog are two surfaces over the
  // same flags — five of the seven here ARE host controls. Those five route to
  // the room-backed store so a change from either surface is the same change,
  // seen by everyone; only `questions` and `handRaise` are engagement's own and
  // stay local. Splitting the write by key is what keeps the two dialogs from
  // drifting apart.
  const engagementPermissions = {
    chat: sendChat,
    questions: allowQuestions,
    reactions: useEmojis,
    handRaise: allowHandRaise,
    audio: useMicrophone,
    video: useVideo,
    screenShare: shareScreen,
  }
  const handleEngagementChange = (
    key: keyof typeof engagementPermissions,
    value: boolean,
  ) => {
    switch (key) {
      case 'chat':        setHostControl('sendChat', value); break
      case 'reactions':   setHostControl('useEmojis', value); break
      case 'audio':       setHostControl('useMicrophone', value); break
      case 'video':       setHostControl('useVideo', value); break
      case 'screenShare': changeHostControl('shareScreen', value); break
      case 'questions':   setEngagementPermission('allowQuestions', value); break
      case 'handRaise':   setEngagementPermission('allowHandRaise', value); break
    }
  }

  const handleSubmitPollVote: ComponentProps<typeof PollVotingDialog>['onSubmitVote'] = (
    pollId,
    optionIds,
  ) => handleVotePoll(pollId, optionIds, localParticipant.id, localParticipant.name)
  const handleEndActivePoll = () => {
    if (activePollId) handleEndPoll(activePollId)
  }
  const activePollResult = activePoll
    ? pollResultFor(activePoll, pollVotes, presentParticipantIds)
    : null

  const layoutMode = useMeetingLayoutStore((s) => s.layoutMode)
  const tileDensity = useMeetingLayoutStore((s) => s.tileDensity)
  const hideTilesWithoutVideo = useMeetingLayoutStore((s) => s.hideTilesWithoutVideo)
  const layoutSyncEnabled = useMeetingLayoutStore((s) => s.layoutSyncEnabled)
  const setLayoutSyncEnabled = useMeetingLayoutStore((s) => s.setLayoutSyncEnabled)
  const setLayoutMode = useMeetingLayoutStore((s) => s.setLayoutMode)
  const setTileDensity = useMeetingLayoutStore((s) => s.setTileDensity)
  const setGridPage = useMeetingLayoutStore((s) => s.setGridPage)
  const setHideTilesWithoutVideo = useMeetingLayoutStore((s) => s.setHideTilesWithoutVideo)

  const {
    createPollOpen, setCreatePollOpen,
    createSurveyOpen, setCreateSurveyOpen,
    pollVotingOpen, setPollVotingOpen,
    pollResultsOpen, setPollResultsOpen,
    backgroundEffectsOpen, setBackgroundEffectsOpen,
    settingsOpen, setSettingsOpen,
    helpOpen, setHelpOpen,
    aboutOpen, setAboutOpen,
    shortcutsOpen, setShortcutsOpen,
    reportOpen, setReportOpen,
    hostControlsOpen, setHostControlsOpen,
    addVideoInputOpen, setAddVideoInputOpen,
    engagementControlsOpen, setEngagementControlsOpen,
    endWebinarDialogOpen, setEndWebinarDialogOpen,
    adjustViewOpen, setAdjustViewOpen,
  } = useMeetingDialogs()

  // The creator never gets the voting form for their own poll, however it was
  // asked for — prompt, Polls panel, or a stale `pollVotingOpen`. The server
  // refuses the vote as well; this keeps the form from being offered at all.
  const pollVotingFormOpen = pollVotingOpen && !isOwnPoll(activePoll, localParticipant.id)

  const {
    breakoutRoomsOpen, setBreakoutRoomsOpen,
    breakoutRoomsAssignmentOpen, setBreakoutRoomsAssignmentOpen,
    breakoutSettings, setBreakoutSettings,
    setActiveBreakoutRooms,
    roomCapacity, assignment,
  } = useBreakoutRooms()

  // The in-meeting background. Writes stay in the meeting — the lobby keeps
  // its own value so the next prejoin preview isn't wearing this one.
  const selectedBackground = useMeetingPreferencesStore((s) => s.meetingBackground)

  // Live camera for the background dialog's preview: the track we are already
  // publishing, and ONLY that.
  //
  // Opening a second capture of the same camera here was wrong twice over. It
  // cost a second segmentation run, and two MediaPipe instances break each
  // other's masks — the published video became a flat rectangle of background
  // with no person in it, for the sender and for everyone else. And because
  // that second capture came from the refcounted MediaStreamContext, closing
  // the dialog could drop the shared count to zero and stop the camera the
  // tiles render from, blacking out your own video.
  //
  // There is deliberately no fallback. If nothing is publishing there is
  // nothing to preview against, and the dialog's placeholder says so honestly —
  // far better than briefly running a second camera, which is what kept
  // reintroducing this.
  const [backgroundPreviewStream, setBackgroundPreviewStream] = useState<MediaStream | null>(null)

  useEffect(() => {
    if (!backgroundEffectsOpen) return
    const published = getPublishedCameraTrack()
    if (!published) return
    setBackgroundPreviewStream(new MediaStream([published]))
    // Warm segmentation on that same camera while the user chooses, so Apply
    // joins a running MediaPipe instead of loading one.
    const releaseWarm = warmSegmentation(published)
    return () => {
      setBackgroundPreviewStream(null)
      releaseWarm()
    }
  }, [backgroundEffectsOpen])
  const setSelectedBackground = useMeetingPreferencesStore((s) => s.setMeetingBackground)
  const settingsTab = useMeetingPreferencesStore((s) => s.settingsTab)
  const setSettingsTab = useMeetingPreferencesStore((s) => s.setSettingsTab)
  const permanentBottomBar = useMeetingPreferencesStore((s) => s.permanentBottomBar)
  const setPermanentBottomBar = useMeetingPreferencesStore((s) => s.setPermanentBottomBar)
  const autoHideNames = useMeetingPreferencesStore((s) => s.autoHideNames)
  const setAutoHideNames = useMeetingPreferencesStore((s) => s.setAutoHideNames)
  const sideDrawerOverContent = useMeetingPreferencesStore((s) => s.sideDrawerOverContent)
  const setSideDrawerOverContent = useMeetingPreferencesStore((s) => s.setSideDrawerOverContent)
  const hiddenSelfVideo = useMeetingPreferencesStore((s) => s.hiddenSelfVideo)
  const setHiddenSelfVideo = useMeetingPreferencesStore((s) => s.setHiddenSelfVideo)
  const inMeetingNotifications = useMeetingPreferencesStore((s) => s.inMeetingNotifications)
  const setInMeetingNotifications = useMeetingPreferencesStore(
    (s) => s.setInMeetingNotifications,
  )
  const inMeetingNotificationSounds = useMeetingPreferencesStore(
    (s) => s.inMeetingNotificationSounds,
  )
  const setInMeetingNotificationSounds = useMeetingPreferencesStore(
    (s) => s.setInMeetingNotificationSounds,
  )
  const autoPictureInPicture = useMeetingPreferencesStore((s) => s.autoPictureInPicture)
  const setAutoPictureInPicture = useMeetingPreferencesStore(
    (s) => s.setAutoPictureInPicture,
  )
  // No Picture-in-Picture on a phone browser, so no Auto PiP setting either:
  // AdvancedSettings drops the row when it gets no change handler.
  const { isMobileDevice: isPhoneDevice } = useResponsiveDevice()
  const hideChatNotifications = useMeetingPreferencesStore((s) => s.hideChatNotifications)
  const setHideChatNotifications = useMeetingPreferencesStore(
    (s) => s.setHideChatNotifications,
  )

  const handleAdjustViewLayoutModeChange: AdjustViewDialogProps['onLayoutModeChange'] = (mode) => {
    if (mode === 'auto') {
      setTileDensity('auto')
      setLayoutMode('auto')
      setGridPage(0)
    } else if (mode === 'tiled') {
      // Density is left alone. It used to pin 'auto' to a literal 16 here,
      // because 'auto' meant "size to the window" and the tiled grid wanted a
      // fixed number. 'auto' IS 16 now — the fifteen from Settings plus you —
      // so pinning it changes nothing on screen while flipping that dropdown
      // from "Auto" to "15" behind the user's back.
      setLayoutMode('tiled')
      setGridPage(0)
    } else {
      setLayoutMode(mode)
    }
  }

  const handleBreakoutCreate: ComponentProps<
    typeof CreateBreakoutRoomsDialog
  >['onCreateRooms'] = (settings) => {
    setBreakoutSettings(settings)
    setBreakoutRoomsOpen(false)
    if (settings.assignmentType === 'automatic') {
      const rooms = assignParticipantsRoundRobin(settings, breakoutAssignableParticipants)
      setActiveBreakoutRooms(rooms)
      setBreakoutRoomsAssignmentOpen(false)
      vclog('presence', 'announcing breakout rooms (auto)', {
        rooms: rooms.length,
        pool: breakoutAssignableParticipants.length,
      })
      // Automatic assignment starts them outright — there is no assignment
      // screen to press Start on, so this is the only place it can happen.
      onStartBreakoutRooms(
        rooms.map((room) => ({
          id: room.id,
          name: room.name,
          participantIds: room.participants.map((p) => p.id),
        })),
        'auto',
        settings.participantLimit,
      )
      return
    }

    // Self walk-in opens the rooms straight away too — its own button says
    // "create and open rooms". There is nothing to arrange first: nobody is
    // placed, the rooms open empty, and everyone is shown the list and picks
    // one. Sending it to the assignment screen left a moderator staring at a
    // Start button that had nothing to enable it.
    if (settings.assignmentType === 'self') {
      const rooms = Array.from({ length: settings.roomCount }, (_, i) => ({
        id: `room-${i + 1}`,
        name: `Room ${i + 1}`,
        isActive: true,
        participants: [],
      }))
      setActiveBreakoutRooms(rooms)
      setBreakoutRoomsAssignmentOpen(false)
      vclog('presence', 'announcing breakout rooms (self walk-in)', { rooms: rooms.length })
      onStartBreakoutRooms(
        rooms.map((room) => ({ id: room.id, name: room.name, participantIds: [] })),
        'self',
        settings.participantLimit,
      )
      return
    }

    setBreakoutRoomsAssignmentOpen(true)
  }

  /**
   * Re-announce the room list without treating it as a fresh start.
   *
   * Same event Start uses — the server replaces the set — but this leaves the
   * screen open, because the moderator is in the middle of arranging it.
   */
  const handleBreakoutRoomsSync = (
    rooms: Array<{ id: string; name: string; participantIds: string[] }>,
  ) => {
    // The mode and the limit come from the ROOM, falling back to the settings
    // only if the room has not said. `breakoutSettings` is this client's memory
    // of a choice it may not have made — a co-host never made it, and a reload
    // loses it — and announcing the rooms without them silently reset the
    // meeting: self walk-in became automatic, and the per-room limit vanished
    // for everybody, just because somebody added a room.
    onStartBreakoutRooms(
      rooms,
      assignment ?? assignmentWireValue(breakoutSettings?.assignmentType),
      roomCapacity ?? breakoutSettings?.participantLimit,
    )
  }

  const handleBreakoutAssignmentStart: ComponentProps<
    typeof BreakoutRoomsAssignment
  >['onStart'] = (rooms) => {
    const activeRooms: ActiveBreakoutRoom[] = rooms.map((room) => ({
      id: room.id,
      name: room.name,
      isActive: true,
      participants: room.participants.map((pid) => {
        const participant = breakoutAssignableParticipants.find((p) => p.id === pid)
        return {
          id: pid,
          name: participant?.name || 'Unknown',
          isHost: participant?.role === 'host',
          audioOn: true,
          videoOn: true,
        }
      }),
    }))
    setActiveBreakoutRooms(activeRooms)
    setBreakoutRoomsAssignmentOpen(false)
    // Tell the room. Everything above is this client's own picture; without
    // this nobody else learns the rooms exist — which is exactly the failure
    // this line records, because when it does not happen the screen still
    // looks right and nothing anywhere says otherwise.
    vclog('presence', 'announcing breakout rooms', {
      rooms: rooms.length,
      assigned: rooms.reduce((n, r) => n + r.participants.length, 0),
    })
    onStartBreakoutRooms(
      rooms.map((room) => ({
        id: room.id,
        name: room.name,
        participantIds: room.participants,
      })),
      assignmentWireValue(breakoutSettings?.assignmentType),
      breakoutSettings?.participantLimit,
    )
  }

  const localUserIsModerator =
    localParticipant.role === 'host' || localParticipant.role === 'moderator'

  usePrefetchDialogs()

  return (
    <>
      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={createPollOpen}
          onClose={() => setCreatePollOpen(false)}
          title="Create Poll"
          hasInputs
        >
          <CreatePollDialog
            open={createPollOpen}
            onClose={() => setCreatePollOpen(false)}
            onCreatePoll={handleCreatePoll}
          />
        </MobileOverlayPanel>
      ) : (
        <CreatePollDialog
          open={createPollOpen}
          onClose={() => setCreatePollOpen(false)}
          onCreatePoll={handleCreatePoll}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={createSurveyOpen}
          onClose={() => setCreateSurveyOpen(false)}
          title="Create Survey"
          hasInputs
        >
          <CreateSurveyDialog
            open={createSurveyOpen}
            onClose={() => setCreateSurveyOpen(false)}
            onCreateSurvey={createSurvey}
            localParticipantName={meetingUserName}
          />
        </MobileOverlayPanel>
      ) : (
        <CreateSurveyDialog
          open={createSurveyOpen}
          onClose={() => setCreateSurveyOpen(false)}
          onCreateSurvey={createSurvey}
          localParticipantName={meetingUserName}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={pollVotingFormOpen}
          onClose={() => setPollVotingOpen(false)}
          title="Vote"
        >
          <PollVotingDialog
            key={activePollId ?? 'no-poll'}
            open={pollVotingFormOpen}
            poll={activePoll}
            onClose={() => setPollVotingOpen(false)}
            onSubmitVote={handleSubmitPollVote}
            hasVoted={Boolean(
              activePollId &&
                pollVotes.some(
                  (v) => v.pollId === activePollId && v.participantId === localParticipant.id,
                ),
            )}
            existingVote={
              activePollId
                ? pollVotes.find(
                    (v) => v.pollId === activePollId && v.participantId === localParticipant.id,
                  )?.optionIds ?? []
                : []
            }
          />
        </MobileOverlayPanel>
      ) : (
        <PollVotingDialog
          key={activePollId ?? 'no-poll'}
          open={pollVotingFormOpen}
          poll={activePoll}
          onClose={() => setPollVotingOpen(false)}
          onSubmitVote={handleSubmitPollVote}
          hasVoted={Boolean(
            activePollId &&
              pollVotes.some(
                (v) => v.pollId === activePollId && v.participantId === localParticipant.id,
              ),
          )}
          existingVote={
            activePollId
              ? pollVotes.find(
                  (v) => v.pollId === activePollId && v.participantId === localParticipant.id,
                )?.optionIds ?? []
              : []
          }
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={pollResultsOpen}
          onClose={() => setPollResultsOpen(false)}
          title="Poll Results"
        >
          <PollResultsDialog
            open={pollResultsOpen}
            poll={activePoll}
            result={activePollResult}
            onClose={() => setPollResultsOpen(false)}
            isModerator={localUserIsModerator}
            onEndPoll={handleEndActivePoll}
          />
        </MobileOverlayPanel>
      ) : (
        <PollResultsDialog
          open={pollResultsOpen}
          poll={activePoll}
          result={activePollResult}
          onClose={() => setPollResultsOpen(false)}
          isModerator={localUserIsModerator}
          onEndPoll={handleEndActivePoll}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={backgroundEffectsOpen}
          onClose={() => setBackgroundEffectsOpen(false)}
          title="Background Effects"
        >
          <BackgroundEffectsDialog
            open={backgroundEffectsOpen}
            selectedBackgroundId={selectedBackground?.id ?? null}
            selectedBackground={selectedBackground}
            previewStream={backgroundPreviewStream}
            onClose={() => setBackgroundEffectsOpen(false)}
            onSelectBackground={(bg) => {
              setSelectedBackground(bg)
              setBackgroundEffectsOpen(false)
            }}
            onClearBackground={() => setSelectedBackground(null)}
            onPreviewBackground={(bg) => preloadBackgroundImage(bg.url)}
          />
        </MobileOverlayPanel>
      ) : (
        <BackgroundEffectsDialog
          open={backgroundEffectsOpen}
          selectedBackgroundId={selectedBackground?.id ?? null}
          selectedBackground={selectedBackground}
          previewStream={backgroundPreviewStream}
          onClose={() => setBackgroundEffectsOpen(false)}
          onSelectBackground={(bg) => {
            setSelectedBackground(bg)
            setBackgroundEffectsOpen(false)
          }}
          onClearBackground={() => setSelectedBackground(null)}
          onPreviewBackground={(bg) => preloadBackgroundImage(bg.url)}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          title="Settings"
        >
          <SettingsDialog
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            settingsTab={settingsTab}
            onTabChange={setSettingsTab}
            permanentBottomBar={permanentBottomBar}
            onPermanentBottomBarChange={setPermanentBottomBar}
            autoHideNames={autoHideNames}
            onAutoHideNamesChange={setAutoHideNames}
            sideDrawerOverContent={sideDrawerOverContent}
            onSideDrawerOverContentChange={setSideDrawerOverContent}
            hiddenSelfVideo={hiddenSelfVideo}
            onHiddenSelfVideoChange={setHiddenSelfVideo}
            notifications={inMeetingNotifications}
            onNotificationsChange={setInMeetingNotifications}
            notificationSounds={inMeetingNotificationSounds}
            onNotificationSoundsChange={setInMeetingNotificationSounds}
            autoPiP={autoPictureInPicture}
            onAutoPiPChange={isPhoneDevice ? undefined : setAutoPictureInPicture}
            hideChatNotifications={hideChatNotifications}
            onHideChatNotificationsChange={setHideChatNotifications}
          />
        </MobileOverlayPanel>
      ) : (
        <SettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          settingsTab={settingsTab}
          onTabChange={setSettingsTab}
          permanentBottomBar={permanentBottomBar}
          onPermanentBottomBarChange={setPermanentBottomBar}
          autoHideNames={autoHideNames}
          onAutoHideNamesChange={setAutoHideNames}
          sideDrawerOverContent={sideDrawerOverContent}
          onSideDrawerOverContentChange={setSideDrawerOverContent}
          hiddenSelfVideo={hiddenSelfVideo}
          onHiddenSelfVideoChange={setHiddenSelfVideo}
          notifications={inMeetingNotifications}
          onNotificationsChange={setInMeetingNotifications}
          notificationSounds={inMeetingNotificationSounds}
          onNotificationSoundsChange={setInMeetingNotificationSounds}
          autoPiP={autoPictureInPicture}
          onAutoPiPChange={isPhoneDevice ? undefined : setAutoPictureInPicture}
          hideChatNotifications={hideChatNotifications}
          onHideChatNotificationsChange={setHideChatNotifications}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel open={helpOpen} onClose={() => setHelpOpen(false)} title="Help">
          <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
        </MobileOverlayPanel>
      ) : (
        <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel open={aboutOpen} onClose={() => setAboutOpen(false)} title="About">
          <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
        </MobileOverlayPanel>
      ) : (
        <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={shortcutsOpen}
          onClose={() => setShortcutsOpen(false)}
          title="Keyboard Shortcuts"
        >
          <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} captionsAvailable={props.captionsAvailable} />
        </MobileOverlayPanel>
      ) : (
        <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} captionsAvailable={props.captionsAvailable} />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          title="Report Issue"
        >
          <ReportDialog open={reportOpen} onOpenChange={setReportOpen} />
        </MobileOverlayPanel>
      ) : (
        <ReportDialog open={reportOpen} onOpenChange={setReportOpen} />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={adjustViewOpen}
          onClose={() => setAdjustViewOpen(false)}
          title="Adjust View"
        >
          <AdjustViewDialog
            open={adjustViewOpen}
            onOpenChange={setAdjustViewOpen}
            layoutMode={layoutMode}
            tileDensity={tileDensity}
            hideTilesWithoutVideo={hideTilesWithoutVideo}
            layoutSyncEnabled={layoutSyncEnabled}
            onLayoutSyncEnabledChange={setLayoutSyncEnabled}
            autoGridTiles={autoGridTiles}
            tiledGridTiles={tiledGridTiles}
            onLayoutModeChange={handleAdjustViewLayoutModeChange}
            onTileDensityChange={setTileDensity}
            onHideTilesWithoutVideoChange={setHideTilesWithoutVideo}
            isModerator={localUserIsModerator}
          />
        </MobileOverlayPanel>
      ) : (
        <AdjustViewDialog
          open={adjustViewOpen}
          onOpenChange={setAdjustViewOpen}
          layoutMode={layoutMode}
          tileDensity={tileDensity}
          hideTilesWithoutVideo={hideTilesWithoutVideo}
          layoutSyncEnabled={layoutSyncEnabled}
          onLayoutSyncEnabledChange={setLayoutSyncEnabled}
          autoGridTiles={autoGridTiles}
          tiledGridTiles={tiledGridTiles}
          onLayoutModeChange={handleAdjustViewLayoutModeChange}
          onTileDensityChange={setTileDensity}
          onHideTilesWithoutVideoChange={setHideTilesWithoutVideo}
          isModerator={localUserIsModerator}
        />
      )}

      {/* Moderators only, and guarded here as well as at the menu entry.
          The publish effect ignores a non-moderator and the server refuses
          `host:set_controls` from one, but the toggle would still write to the
          LOCAL mirror — so an attendee who reached this dialog could re-enable
          their own mic button by flipping the switch the host had closed.
          Nothing renders it for them today; this makes that a property of the
          dialog rather than of one call site. */}
      {localUserIsModerator && (
        <HostControlsDialog
          open={hostControlsOpen}
          onOpenChange={setHostControlsOpen}
          permissions={hostPermissions}
          onPermissionChange={changeHostControl}
        />
      )}

      {pendingShareOff && (
        <ConfirmDialog
          open
          title={t(
            pendingShareOff === 'shareScreen'
              ? 'dialogs.disableScreenSharingTitle'
              : 'dialogs.disableFileSharingTitle',
          )}
          description={t(
            pendingShareOff === 'shareScreen'
              ? 'dialogs.disableScreenSharingWhileSharing'
              : 'dialogs.disableFileSharingWhileSharing',
          )}
          destructive
          onConfirm={() => {
            setHostControl(pendingShareOff, false)
            setPendingShareOff(null)
          }}
          onCancel={() => setPendingShareOff(null)}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={addVideoInputOpen}
          onClose={() => setAddVideoInputOpen(false)}
          title="Add Video Input"
        >
          <AddNewVideoInputDialog
            open={addVideoInputOpen}
            onOpenChange={setAddVideoInputOpen}
            inputs={props.videoInputs}
            maxInputs={props.maxVideoInputs}
            onAdd={props.onAddVideoInput}
            onRemove={props.onRemoveVideoInput}
          />
        </MobileOverlayPanel>
      ) : (
        <AddNewVideoInputDialog
          open={addVideoInputOpen}
          onOpenChange={setAddVideoInputOpen}
          inputs={props.videoInputs}
          maxInputs={props.maxVideoInputs}
          onAdd={props.onAddVideoInput}
          onRemove={props.onRemoveVideoInput}
        />
      )}

      <EngagementControlsDialog
        open={engagementControlsOpen}
        onOpenChange={setEngagementControlsOpen}
        permissions={engagementPermissions}
        onPermissionChange={handleEngagementChange}
      />

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={endWebinarDialogOpen}
          onClose={() => setEndWebinarDialogOpen(false)}
          title="End Webinar"
        >
          <EndWebinarDialog
            open={endWebinarDialogOpen}
            onOpenChange={setEndWebinarDialogOpen}
            onEndWebinar={() => onLeaveMeeting()}
          />
        </MobileOverlayPanel>
      ) : (
        <EndWebinarDialog
          open={endWebinarDialogOpen}
          onOpenChange={setEndWebinarDialogOpen}
          onEndWebinar={() => onLeaveMeeting()}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={breakoutRoomsOpen}
          onClose={() => setBreakoutRoomsOpen(false)}
          title="Create Breakout Rooms"
        >
          <CreateBreakoutRoomsDialog
            open={breakoutRoomsOpen}
            onOpenChange={setBreakoutRoomsOpen}
            onCancel={() => setBreakoutRoomsOpen(false)}
            onCreateRooms={handleBreakoutCreate}
          />
        </MobileOverlayPanel>
      ) : (
        <CreateBreakoutRoomsDialog
          open={breakoutRoomsOpen}
          onOpenChange={setBreakoutRoomsOpen}
          onCancel={() => setBreakoutRoomsOpen(false)}
          onCreateRooms={handleBreakoutCreate}
        />
      )}

      {isMobileOrLandscape ? (
        <MobileOverlayPanel
          open={breakoutRoomsAssignmentOpen}
          onClose={() => setBreakoutRoomsAssignmentOpen(false)}
          title="Assign Participants"
        >
          <BreakoutRoomsAssignment
            started={breakoutStarted}
            onEnd={onEndBreakoutRooms}
            onMove={onMoveBreakoutParticipant}
            liveRooms={activeBreakoutRooms}
            selfSelect={breakoutSettings?.assignmentType === 'self'}
          // From the room, not from the dialog that created it: the settings are
          // this client's memory of a choice it may not have made, and are gone
          // after a reload. The room's own limit is neither.
          capacity={roomCapacity ?? breakoutSettings?.participantLimit ?? null}
          onRoomFull={onBreakoutRoomFull}
            onSyncRooms={handleBreakoutRoomsSync}
            open={breakoutRoomsAssignmentOpen}
            onOpenChange={setBreakoutRoomsAssignmentOpen}
            roomCount={breakoutSettings?.roomCount ?? 2}
            participants={breakoutAssignableParticipants}
            onBack={() => setBreakoutRoomsAssignmentOpen(false)}
            onStart={handleBreakoutAssignmentStart}
            onReset={() => {}}
          />
        </MobileOverlayPanel>
      ) : (
        <BreakoutRoomsAssignment
          open={breakoutRoomsAssignmentOpen}
          onOpenChange={setBreakoutRoomsAssignmentOpen}
          roomCount={breakoutSettings?.roomCount ?? 2}
          participants={breakoutAssignableParticipants}
          onBack={() => setBreakoutRoomsAssignmentOpen(false)}
          onStart={handleBreakoutAssignmentStart}
          started={breakoutStarted}
          onEnd={onEndBreakoutRooms}
          onMove={onMoveBreakoutParticipant}
          liveRooms={activeBreakoutRooms}
          selfSelect={breakoutSettings?.assignmentType === 'self'}
          // From the room, not from the dialog that created it: the settings are
          // this client's memory of a choice it may not have made, and are gone
          // after a reload. The room's own limit is neither.
          capacity={roomCapacity ?? breakoutSettings?.participantLimit ?? null}
          onRoomFull={onBreakoutRoomFull}
          onSyncRooms={handleBreakoutRoomsSync}
          onReset={() => {}}
        />
      )}
    </>
  )
}
